import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TextInput, Button, Heading, Text, View } from '@smarterservices/smarter-ui';

/**
 * MissingParametersForm Component
 * 
 * Displays a user-friendly form for entering missing LiveKit connection parameters.
 * Allows users to input required parameters and submit to update the URL.
 * 
 * @param {Object} props - Component props
 * @param {Array<string>} props.missingParams - Array of missing parameter names
 * @param {Function} props.onSubmit - Callback when form is submitted with values
 * @param {number} props.retryCount - Current retry count
 * @param {number} props.maxRetries - Maximum retry attempts
 * @returns {React.ReactElement} The rendered form component
 */
function MissingParametersForm({ missingParams, onSubmit, retryCount, maxRetries }) {
    const [formValues, setFormValues] = useState({
        url: '',
        token: '',
        room: ''
    });

    const [errors, setErrors] = useState({});

    const handleInputChange = (paramName, value) => {
        setFormValues(prev => ({
            ...prev,
            [paramName]: value
        }));

        // Clear error for this field when user types
        if (errors[paramName]) {
            setErrors(prev => ({
                ...prev,
                [paramName]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        missingParams.forEach(param => {
            const value = formValues[param]?.trim();

            if (!value) {
                newErrors[param] = `${param} is required`;
            } else if (param === 'url') {
                // Validate URL format
                if (!value.startsWith('wss://') && !value.startsWith('ws://')) {
                    newErrors[param] = 'URL must start with wss:// or ws://';
                }
            } else if (param === 'token') {
                // Basic token validation
                if (value.length < 10) {
                    newErrors[param] = 'Token appears to be invalid (too short)';
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            onSubmit(formValues);
        }
    };

    const getParameterLabel = (param) => {
        const labels = {
            url: 'LiveKit Server URL',
            token: 'Access Token',
            room: 'Room Name'
        };
        return labels[param] || param;
    };

    const getParameterPlaceholder = (param) => {
        const placeholders = {
            url: 'wss://your-livekit-server.com',
            token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
            room: 'my-room-name'
        };
        return placeholders[param] || '';
    };

    const getParameterHelp = (param) => {
        const help = {
            url: 'The WebSocket URL of your LiveKit server (must start with wss:// or ws://)',
            token: 'Your LiveKit access token for authentication',
            room: 'The name of the room to connect to'
        };
        return help[param] || '';
    };

    return (
        <View
            as="div"
            padding="large"
            background="primary"
            borderRadius="medium"
            shadow="resting"
            maxWidth="600px"
            margin="medium auto 0 auto"
            textAlign="start"
        >
            <Heading level="h2" margin="0 0 small 0">
                Missing Required Parameters
            </Heading>

            <Text as="p" size="medium" margin="0 0 medium 0">
                Please provide the following LiveKit connection parameters to start the recording session:
            </Text>

            <form onSubmit={handleSubmit}>
                <View as="div" margin="0 0 medium 0">
                    {missingParams.map((param) => (
                        <View key={param} as="div" margin="0 0 medium 0">
                            <TextInput
                                renderLabel={getParameterLabel(param)}
                                value={formValues[param]}
                                onChange={(e) => handleInputChange(param, e.target.value)}
                                placeholder={getParameterPlaceholder(param)}
                                messages={errors[param] ? [{ text: errors[param], type: 'error' }] : []}
                                isRequired
                                width="100%"
                            />
                            <Text as="div" size="small" color="secondary" margin="x-small 0 0 0">
                                {getParameterHelp(param)}
                            </Text>
                        </View>
                    ))}
                </View>

                <View as="div" margin="medium 0 0 0">
                    <Button
                        type="submit"
                        color="primary"
                        margin="0 small 0 0"
                    >
                        Connect to Recording Session
                    </Button>

                    {retryCount < maxRetries && (
                        <Button
                            type="button"
                            color="secondary"
                            onClick={() => onSubmit(null)}
                        >
                            Retry ({retryCount + 1}/{maxRetries})
                        </Button>
                    )}
                </View>
            </form>

            <View
                as="details"
                margin="large 0 0 0"
                padding="small"
                background="secondary"
                borderRadius="small"
            >
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Example URL Format
                </summary>
                <Text
                    as="pre"
                    size="small"
                    margin="small 0 0 0"
                    style={{
                        overflow: 'auto',
                        padding: '10px',
                        background: '#f5f5f5',
                        borderRadius: '4px'
                    }}
                >
                    {`/v1/installs/{installSid}/exams/{examSid}/sessions/{sessionSid}/recording?url=wss://your-server.livekit.cloud&token=your-jwt-token&room=your-room-name&showTrackSid=true`}
                </Text>
            </View>

            <View as="div" margin="medium 0 0 0">
                <Text as="p" size="small" color="secondary">
                    <strong>Note:</strong> These parameters will be added to the current URL.
                    Make sure your LiveKit server is running and accessible.
                </Text>
            </View>
        </View>
    );
}

MissingParametersForm.propTypes = {
    missingParams: PropTypes.arrayOf(PropTypes.string).isRequired,
    onSubmit: PropTypes.func.isRequired,
    retryCount: PropTypes.number.isRequired,
    maxRetries: PropTypes.number.isRequired
};

export default MissingParametersForm;
